import { Button, ButtonLoading } from '@tovala/component-library'
import { ReactNode, useCallback, useMemo, useState } from 'react'
import {
  MealSummary,
  TermStatusSubTerm,
  useChangeSubscriptionStatus,
  useInvalidateOrderEstimate,
  useInvalidateTermStatuses,
  useOrderEstimate,
  UserSubscriptionType,
  UserV1,
  useSkipWeek,
  useUpsertUserTermStatus,
  useUserSubscriptionTypes,
} from '@tovala/browser-apis-combinedapi'
import { useForm } from 'react-hook-form'
import { useLocation } from 'react-router-dom'

import { DATE_FORMATS, formatDate } from '../../utils/dates'
import { ErrorCodeMessageMapCombinedAPI, UserTerm } from 'types/internal'
import { events, getSourceIDFromPathname } from '../../analytics/events'
import { formatCentsToDollars } from 'utils/currency'
import { getMealSelectionIDs } from 'utils/meals'
import { getOrderCutoffFormattedDate, isTermDelivering } from 'utils/terms'
import { track } from 'utils/analytics'

import { useHasPastOrders } from 'hooks/orderHistory'
import { useUpdateTermStatusesToNewDefaults } from 'hooks/terms'
import { useOverlays } from 'contexts/overlays'
import { useToast } from 'contexts/toast'
import { useTypeformPopup } from 'hooks/typeform'
import { useUnavailableMealSelectionsForDeliveryDayChange } from 'hooks/menus'
import { useUser } from 'contexts/user'
import APIErrorDisplay from 'components/common/APIErrorDisplay'
import ConfirmationDialog, {
  ConfirmationBody,
  ConfirmationButtons,
  ConfirmationHeader,
} from 'components/common/ConfirmationDialog'
import DeliveryDays from './DeliveryDays'
import MealPlans from './MealPlans'
import Sidebar, {
  SidebarBody,
  SidebarButtons,
  SidebarHeader,
} from 'components/common/Sidebar'
import TermSkippedSidebarBody from './TermSkippedSidebarBody'
import TermSoldOutSidebarBody from './TermSoldOutSIdebarBody'
import WarningDiamondIcon from 'components/common/icons/WarningDiamondIcon'

interface FormData {
  subscriptionTypeID: string
  subTermID: string
}

type ConfirmationDialogData =
  | {
      type: 'tooManyMeals'
      data: {
        selectionsCount: number
        maxSelections: number
      }
    }
  | {
      type: 'mealsUnavailable'
      data: {
        unavailableMealSelections: MealSummary[]
      }
    }
  | {
      type: 'tooManyMealsAndMealsUnavailable'
      data: {
        unavailableMealSelections: MealSummary[]
      }
    }
  | {
      type: 'discardChanges'
    }
  | null

const EDIT_TERM_STATUS_ERRORS: ErrorCodeMessageMapCombinedAPI = {
  Fallback: {
    helpToFix: 'Please try again.',
    why: "We couldn't update your meal plan due to a technical issue on our end.",
  },
  UserSkippedTerm: {
    helpToFix: 'Please refresh the page and try again.',
    why: "We couldn't update this delivery because it has been skipped.",
  },
}

const LOAD_MEAL_PLANS_ERRORS: ErrorCodeMessageMapCombinedAPI = {
  Fallback: {
    helpToFix: 'Please close and reopen this sidebar to try again.',
    why: "We couldn't load our available meal plans due to a technical issue on our end.",
  },
}

const SKIP_WEEK_ERRORS: ErrorCodeMessageMapCombinedAPI = {
  Fallback: {
    helpToFix: 'Please try again.',
    why: "We couldn't skip this week due to a technical issue on our end.",
  },
}

const FORM_ID = 'edit-term-status-form'

const EditTermStatusSidebar = ({
  onClose,
  selectedUserTerm,
}: {
  onClose(): void
  selectedUserTerm: UserTerm
}) => {
  if (selectedUserTerm.isStaticSkipped) {
    return (
      <SidebarContainer
        onCloseSidebar={onClose}
        selectedUserTerm={selectedUserTerm}
      >
        <TermSoldOutSidebarBody onClickMyOrders={onClose} />
      </SidebarContainer>
    )
  } else if (selectedUserTerm.isSkipped) {
    return (
      <SidebarContainer
        onCloseSidebar={onClose}
        selectedUserTerm={selectedUserTerm}
      >
        <TermSkippedSidebarBody selectedUserTerm={selectedUserTerm} />
      </SidebarContainer>
    )
  }

  return (
    <EditTermStatus
      onClose={() => {
        onClose()
      }}
      selectedUserTerm={selectedUserTerm}
    />
  )
}

export default EditTermStatusSidebar

const SidebarContainer = ({
  buttons,
  children,
  onCloseSidebar,
  selectedUserTerm,
}: {
  buttons?: ReactNode
  children: ReactNode
  onCloseSidebar(): void
  selectedUserTerm: UserTerm
}) => {
  return (
    <Sidebar onCloseSidebar={onCloseSidebar}>
      <SidebarHeader
        onClickClose={() => {
          track(events.ADJUST_POP_UP_DISMISS)
          onCloseSidebar()
        }}
      >
        Week of{' '}
        {formatDate(selectedUserTerm.startDate, {
          format: DATE_FORMATS.MONTH_DAY,
        })}
      </SidebarHeader>

      <SidebarBody>
        <div className="px-6 py-10 md:px-4">{children}</div>
      </SidebarBody>

      {buttons}
    </Sidebar>
  )
}

const EditTermStatus = ({
  onClose,
  selectedUserTerm,
}: {
  onClose(): void
  selectedUserTerm: UserTerm
}) => {
  const { pathname } = useLocation()

  const { openToast } = useToast()

  const { user } = useUser()

  const { ConfirmationDialogs, confirm } = useConfirm()

  const [submittingUpdates, setSubmittingUpdates] = useState<
    'allOrders' | 'thisOrder' | null
  >(null)

  const { resetOrderEstimate } = useInvalidateOrderEstimate()

  const {
    data: orderEstimate,
    isError: hasLoadOrderEstimateError,
    isPending: isOrderEstimatePending,
  } = useOrderEstimate({
    termID:
      selectedUserTerm.readyForView && isTermDelivering(selectedUserTerm)
        ? selectedUserTerm.termID
        : undefined,
    userID: user.id,
  })

  const mealSelectionIDs = getMealSelectionIDs({ selectedUserTerm })

  const { unavailableMealSelections } =
    useUnavailableMealSelectionsForDeliveryDayChange({ term: selectedUserTerm })

  const {
    data: getUserSubscriptionTypesResponse,
    error: loadUserSubscriptionTypesError,
    isError: hasLoadUserSubscriptionTypesError,
  } = useUserSubscriptionTypes({
    userID: user.id,
  })
  const userSubscriptionTypes =
    getUserSubscriptionTypesResponse?.subscription_types ?? []

  const {
    error: changeSubscriptionStatusError,
    isError: hasChangeSubscriptionStatusError,
    mutateAsync: changeSubscriptionStatusAsync,
  } = useChangeSubscriptionStatus()

  const {
    error: upsertUserTermStatusError,
    isError: hasUpsertUserTermStatusError,
    mutateAsync: upsertUserTermStatusAsync,
  } = useUpsertUserTermStatus()

  const { updateTermStatusesToNewDefaults } =
    useUpdateTermStatusesToNewDefaults()

  const {
    control,
    formState: { isDirty },
    handleSubmit,
    setValue,
    watch,
  } = useForm<FormData>({
    defaultValues: {
      subscriptionTypeID: selectedUserTerm.subscriptionType?.id ?? '',
      subTermID: selectedUserTerm.selectedSubTermID,
    },
  })
  const selectedSubscriptionTypeID = watch('subscriptionTypeID')
  const selectedSubTermID = watch('subTermID')

  const deliveryOptions = selectedUserTerm.subTerms.map((subTerm) => ({
    label: formatDate(subTerm.deliveryDate, {
      format: DATE_FORMATS.DOW_FULL_MONTH_DAY,
    }),
    value: subTerm.subTermID,
  }))

  const selectedSubscription = userSubscriptionTypes.find((type) => {
    return type.id === selectedSubscriptionTypeID
  })

  const getConfirmationDialogData = () => {
    const selectedSubscription = userSubscriptionTypes.find(
      (type) => type.id === selectedSubscriptionTypeID,
    )
    if (!selectedSubscription) {
      throw new Error('Selected subscription not found')
    }

    let confirmationDialogData: ConfirmationDialogData = null

    const isChangingDeliveryDay =
      selectedUserTerm.selectedSubTermID !== selectedSubTermID
    const hasUnavailableMealSelections =
      isChangingDeliveryDay && unavailableMealSelections.length > 0
    const hasTooManySelections =
      mealSelectionIDs.length > selectedSubscription.max_selections

    if (hasUnavailableMealSelections && hasTooManySelections) {
      confirmationDialogData = {
        type: 'tooManyMealsAndMealsUnavailable',
        data: { unavailableMealSelections },
      }
    } else if (hasUnavailableMealSelections) {
      confirmationDialogData = {
        type: 'mealsUnavailable',
        data: { unavailableMealSelections },
      }
    } else if (hasTooManySelections) {
      confirmationDialogData = {
        type: 'tooManyMeals',
        data: {
          maxSelections: selectedSubscription.max_selections,
          selectionsCount: mealSelectionIDs.length,
        },
      }
    }

    return confirmationDialogData
  }

  const onUpdateAllOrders = async (data: FormData) => {
    track(events.ADJUST_UPDATE_ORDER_ALL)
    track(events.TAPS_APPLY_TO_ALL_ORDERS, {
      original_order_size: selectedUserTerm.subscriptionType?.maxSelections,
      new_order_size: selectedSubscription
        ? selectedSubscription.max_selections
        : undefined,
      term_id: selectedUserTerm.termID,
    })

    const isConfirmed = await confirm(getConfirmationDialogData())

    if (!isConfirmed) {
      return
    }

    const selectedSubTerm = selectedUserTerm.subTerms.find(
      (subTerm) => subTerm.subTermID === data.subTermID,
    )

    if (!selectedSubTerm) {
      return
    }
    setSubmittingUpdates('allOrders')

    const selectedShipPeriod = selectedSubTerm.shipPeriod
    const subscription = {
      defaultShipPeriod: selectedShipPeriod,
      subscriptionTypeID: data.subscriptionTypeID,
    }

    await changeSubscriptionStatusAsync(
      {
        data: subscription,
        subscriptionStatus: 'activate',
        userID: user.id,
      },
      {
        onSuccess: async () => {
          try {
            await updateTermStatusesToNewDefaults({
              shipPeriod: selectedShipPeriod,
              subscriptionTypeID: data.subscriptionTypeID,
            })

            openToast({
              heading: 'Updates Saved',
              message: 'Updates applied to all orders.',
              type: 'success',
            })

            onClose()

            // eslint-disable-next-line @typescript-eslint/no-unused-vars
          } catch (error) {
            setSubmittingUpdates(null)

            openToast({
              goodErrorMessaging: {
                helpToFix: 'Please reload the page and try again',
                why: "We couldn't update all orders due to a technical issue on our end.",
              },
              type: 'error',
            })
          }
        },
        onError: () => {
          setSubmittingUpdates(null)
        },
      },
    )
  }

  const onUpdateThisOrder = async (data: FormData) => {
    const isConfirmed = await confirm(getConfirmationDialogData())

    if (!isConfirmed) {
      return
    }

    setSubmittingUpdates('thisOrder')

    const previousPlanSize =
      selectedUserTerm.subscriptionType?.maxSelections ?? 0

    await upsertUserTermStatusAsync(
      {
        data: {
          notes: '',
          subscriptionTypeID: data.subscriptionTypeID,
          subTermID: data.subTermID,
          termID: selectedUserTerm.termID,
          userID: user.id,
        },
        termStatusID: selectedUserTerm.id ?? '',
        userID: user.id,
      },
      {
        onSuccess: (...args) => {
          const termStatus = args[0]
          const selectedSubTerm = termStatus.subTerms.find(
            (subTerm) => subTerm.subTermID === termStatus.selectedSubTermID,
          )
          const deliveryDate = selectedSubTerm?.deliveryDate

          track(events.USER_EDITS_WEEK, {
            previous_plan_size: previousPlanSize,
            plan_size: termStatus.subscriptionType?.maxSelections,
            delivery_day_of_week: deliveryDate
              ? formatDate(deliveryDate, {
                  format: DATE_FORMATS.DOW_FULL,
                })
              : undefined,
            ship_period: selectedSubTerm?.shipPeriod,
            term_id: termStatus.termID,
          })

          resetOrderEstimate(selectedUserTerm.termID, user.id)

          if (
            deliveryDate &&
            deliveryDate !== selectedUserTerm.selectedSubTerm?.deliveryDate
          ) {
            track(events.USER_EDITS_DELIVERY, {
              delivery_day_of_week: deliveryDate,
            })
          }

          openToast({
            heading: 'Updates Saved',
            message: `Updates applied to your ${
              deliveryDate
                ? formatDate(deliveryDate, {
                    format: DATE_FORMATS.DOW_MONTH_DAY,
                  })
                : ''
            } delivery.`,
            type: 'success',
          })

          onClose()
        },
        onError: () => {
          setSubmittingUpdates(null)
        },
      },
    )
  }

  const handleOnClickClose = async () => {
    if (isDirty) {
      const isConfirmed = await confirm({ type: 'discardChanges' })
      if (isConfirmed) {
        onClose()
      }
    } else {
      onClose()
    }
  }

  return (
    <SidebarContainer
      buttons={
        <SidebarButtons>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 p-6 md:p-4">
              {hasChangeSubscriptionStatusError ? (
                <div className="col-span-2">
                  <APIErrorDisplay
                    error={changeSubscriptionStatusError}
                    errorCodeMessageMap={EDIT_TERM_STATUS_ERRORS}
                  />
                </div>
              ) : hasUpsertUserTermStatusError ? (
                <div className="col-span-2">
                  <APIErrorDisplay
                    error={upsertUserTermStatusError}
                    errorCodeMessageMap={EDIT_TERM_STATUS_ERRORS}
                  />
                </div>
              ) : null}

              <ButtonLoading
                buttonStyle="stroke-filled"
                disabled={!isDirty || submittingUpdates === 'thisOrder'}
                id="updateAllOrders"
                isLoading={submittingUpdates === 'allOrders'}
                onClick={handleSubmit(onUpdateAllOrders)}
                size="large"
                type="button"
              >
                Update All Orders
              </ButtonLoading>
              <ButtonLoading
                buttonStyle="dark"
                disabled={!isDirty || submittingUpdates === 'allOrders'}
                form={FORM_ID}
                isLoading={submittingUpdates === 'thisOrder'}
                size="large"
                type="submit"
              >
                Update This Order
              </ButtonLoading>
            </div>
          </div>
        </SidebarButtons>
      }
      onCloseSidebar={handleOnClickClose}
      selectedUserTerm={selectedUserTerm}
    >
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-k/20_125" id="delivery-day">
          Delivery Day
        </h3>

        <SkipDelivery
          onChangeField={(
            fieldName: 'subscriptionTypeID' | 'subTermID',
            value: string,
          ) => {
            setValue(fieldName, value, { shouldDirty: true })
          }}
          onClose={onClose}
          selectedUserTerm={selectedUserTerm}
          user={user}
          userSubscriptionTypes={userSubscriptionTypes}
        />
      </div>

      <form id={FORM_ID} onSubmit={handleSubmit(onUpdateThisOrder)}>
        {deliveryOptions && (
          <div className="mb-10">
            <DeliveryDays
              control={control}
              deliveryOptions={deliveryOptions}
              name="subTermID"
            />
          </div>
        )}

        <h3 className="mb-4 text-k/20_125" id="meals">
          Meals per Order
        </h3>

        {userSubscriptionTypes.length > 0 ? (
          <div
            aria-labelledby="meals"
            className="mb-10 grid auto-rows-[96px] grid-cols-[repeat(auto-fill,88px)] gap-4"
            role="radiogroup"
          >
            <MealPlans<FormData>
              control={control}
              mealPlans={userSubscriptionTypes}
              name="subscriptionTypeID"
              onChangePlan={(mealPlan) => {
                track(events.USER_EDITS_PLAN, {
                  plan_size: mealPlan.max_selections,
                  source_id: getSourceIDFromPathname(pathname),
                })
              }}
            />
          </div>
        ) : hasLoadUserSubscriptionTypesError ? (
          <APIErrorDisplay
            error={loadUserSubscriptionTypesError}
            errorCodeMessageMap={LOAD_MEAL_PLANS_ERRORS}
          />
        ) : null}

        {selectedUserTerm.readyForView ? (
          hasLoadOrderEstimateError || hasLoadUserSubscriptionTypesError ? (
            <PriceUnavailable />
          ) : selectedSubscription &&
            orderEstimate &&
            !isOrderEstimatePending ? (
            <OrderSummary
              mealPriceCents={orderEstimate.subscriptionMealPriceCents}
              selectedSubscription={selectedSubscription}
            />
          ) : null
        ) : null}

        <p className="pt-6 text-center text-grey-9">
          Select meals or skip this delivery by
          <br />
          {getOrderCutoffFormattedDate({
            orderByDate: selectedUserTerm.orderByDate,
          })}
        </p>
      </form>

      <ConfirmationDialogs />
    </SidebarContainer>
  )
}

const SkipDelivery = ({
  onChangeField,
  onClose,
  selectedUserTerm,
  user,
  userSubscriptionTypes,
}: {
  onChangeField(
    fieldName: 'subscriptionTypeID' | 'subTermID',
    value: string,
  ): void
  onClose(): void
  selectedUserTerm: UserTerm
  user: UserV1
  userSubscriptionTypes: UserSubscriptionType[]
}) => {
  const { invalidateUserTermStatuses } = useInvalidateTermStatuses()

  const { hasViewedSkipSurvey, setHasViewedSkipSurvey } = useOverlays()
  const { openToast } = useToast()

  const [openSkipWeekConfirmation, setOpenSkipWeekConfirmation] =
    useState(false)

  const hasPastOrders = useHasPastOrders({ userID: user.id })

  const {
    selectedSubTerm,
    selectedSubTermID,
    subscriptionType,
    subTerms,
    termID,
  } = selectedUserTerm

  const alternativeSubTerm = subTerms?.find(
    (subTerm) => subTerm.subTermID !== selectedSubTermID,
  )
  const termSubscriptionIndex = subscriptionType
    ? userSubscriptionTypes
        .map((type) => type.max_selections)
        .indexOf(subscriptionType.maxSelections)
    : -1
  const smallerSubscription =
    termSubscriptionIndex > 0
      ? userSubscriptionTypes[termSubscriptionIndex - 1]
      : undefined

  const shouldConfirmSkipDelivery = !!(
    smallerSubscription || alternativeSubTerm
  )

  const deliveryDate = selectedSubTerm?.deliveryDate

  const openToastSkipSuccess = useCallback(() => {
    openToast({
      heading: 'Delivery Skipped',
      message: deliveryDate
        ? `You will not receive a delivery ${formatDate(deliveryDate, {
            format: DATE_FORMATS.DOW_MONTH_DAY,
          })}`
        : 'Your delivery was skipped successfully.',
      type: 'success',
    })

    onClose()
    setHasViewedSkipSurvey(true)
  }, [deliveryDate, onClose, openToast, setHasViewedSkipSurvey])

  const hiddenFields = useMemo(
    () => ({
      termid: `${termID}`,
    }),
    [termID],
  )

  const { openTypeformPopup } = useTypeformPopup({
    userID: `${user.id}`,
  })

  // In the event the user is skipping multiple weeks, we should only show the skip survey once per session
  // and only to users that have at least one meal order
  const shouldShowSkipSurvey = !hasViewedSkipSurvey && hasPastOrders

  const {
    error: skipWeekError,
    isError: hasSkipWeekError,
    isPending: isSkippingWeek,
    mutate: skipWeek,
  } = useSkipWeek({
    onSuccess: () => {
      invalidateUserTermStatuses(user.id)

      setOpenSkipWeekConfirmation(false)

      track(events.SKIPS_WEEK, {
        term_id: selectedUserTerm.termID,
      })

      if (shouldShowSkipSurvey) {
        openTypeformPopup({
          hiddenFields,
          onClose: openToastSkipSuccess,
          survey: 'skip',
        })
      } else {
        openToastSkipSuccess()
      }
    },
  })

  const handleSkip = () => {
    skipWeek({
      data: {
        termid: selectedUserTerm.termID,
      },
      userID: user.id,
    })
  }

  return (
    <>
      <ButtonLoading
        buttonStyle="white"
        isLoading={isSkippingWeek}
        onClick={() => {
          track(events.SKIP_DELIVERY_CTA)

          if (shouldConfirmSkipDelivery) {
            setOpenSkipWeekConfirmation(true)
          } else {
            handleSkip()
          }
        }}
        size="small"
      >
        Skip Delivery
      </ButtonLoading>
      {openSkipWeekConfirmation && (
        <SkipWeekConfirmation
          alternativeSubTerm={alternativeSubTerm}
          onClickClose={() => {
            setOpenSkipWeekConfirmation(false)
          }}
          onClickFewerMeals={(smallerSubscription) => {
            onChangeField('subscriptionTypeID', smallerSubscription.id)
            setOpenSkipWeekConfirmation(false)
          }}
          onClickSkip={handleSkip}
          onClickSwitchDelivery={(alternativeSubterm) => {
            onChangeField('subTermID', alternativeSubterm.subTermID)
            setOpenSkipWeekConfirmation(false)
          }}
          selectedUserTerm={selectedUserTerm}
          skipWeekError={hasSkipWeekError ? skipWeekError : null}
          smallerSubscription={smallerSubscription}
        />
      )}
    </>
  )
}

const useConfirm = () => {
  const [promise, setPromise] = useState<{
    resolve: (value: boolean) => void
    data: ConfirmationDialogData
  } | null>(null)

  const confirm = (data: ConfirmationDialogData) =>
    data === null
      ? Promise.resolve(true)
      : new Promise((resolve) => {
          setPromise({ data, resolve })
        })

  const handleClose = () => setPromise(null)

  const handleConfirm = () => {
    promise?.resolve(true)
    handleClose()
  }

  const handleCancel = () => {
    promise?.resolve(false)
    handleClose()
  }

  const ConfirmationDialogs = () => {
    if (!promise || !promise.data) {
      return null
    }

    const { type } = promise.data

    if (type === 'tooManyMeals') {
      const { selectionsCount, maxSelections } = promise.data.data

      return (
        <TooManyMealsConfirmation
          maxSelections={maxSelections}
          onClickClose={handleCancel}
          onClickConfirm={handleConfirm}
          selectionsCount={selectionsCount}
        />
      )
    }
    if (type === 'mealsUnavailable') {
      const { unavailableMealSelections } = promise.data.data
      return (
        <MealsUnavailableConfirmation
          onClickClose={handleCancel}
          onClickConfirm={handleConfirm}
          unavailableMealSelections={unavailableMealSelections}
        />
      )
    }
    if (type === 'tooManyMealsAndMealsUnavailable') {
      const { unavailableMealSelections } = promise.data.data
      return (
        <TooManyMealsAndMealsUnavailableConfirmation
          onClickClose={handleCancel}
          onClickConfirm={handleConfirm}
          unavailableMealSelections={unavailableMealSelections}
        />
      )
    }
    if (type === 'discardChanges') {
      return (
        <DiscardChangesConfirmation
          onClickClose={handleCancel}
          onClickConfirm={handleConfirm}
        />
      )
    }
    return null
  }
  return { ConfirmationDialogs, confirm }
}

const OrderSummary = ({
  mealPriceCents,
  selectedSubscription,
}: {
  mealPriceCents: number
  selectedSubscription: UserSubscriptionType
}) => {
  const mealCostCents = mealPriceCents * selectedSubscription.max_selections
  const shippingCents = selectedSubscription.shippingCents
  const totalCents = mealCostCents + shippingCents

  return (
    <div className="divide-y">
      <h3 className="mb-4 text-k/20_125">Order Summary</h3>

      <div className="flex justify-between py-4 text-k/13_120 text-grey-9">
        <span>Meal Cost</span>
        <span>{formatCentsToDollars(mealCostCents)}</span>
      </div>

      <div className="flex justify-between py-4 text-k/13_120 text-grey-9">
        <span>Shipping</span>
        <span>
          {shippingCents > 0 ? formatCentsToDollars(shippingCents) : 'Free'}
        </span>
      </div>

      <div className="flex justify-between pt-4 text-k/16_125">
        <span>Estimated Total</span>
        <span>{formatCentsToDollars(totalCents)}</span>
      </div>
    </div>
  )
}

const PriceUnavailable = () => {
  return (
    <div className="flex space-x-2 rounded-lg bg-grey-3 px-3 py-4">
      <div className="size-8 shrink-0 text-red">
        <WarningDiamondIcon />
      </div>
      <div className="text-k/18_120 md:text-k/16_125">
        <p className="font-semibold">Price unavailable</p>
        <p>
          You can still skip or adjust. Your full total will be communicated
          before your box ships.
        </p>
      </div>
    </div>
  )
}

export const TooManyMealsConfirmation = ({
  maxSelections,
  onClickClose,
  onClickConfirm,
  selectionsCount,
}: {
  maxSelections: number | undefined
  onClickClose(): void
  onClickConfirm(): void
  selectionsCount: number
}) => {
  const mealsDifference =
    maxSelections && maxSelections < selectionsCount
      ? selectionsCount - maxSelections
      : undefined

  return (
    <ConfirmationDialog onRequestClose={onClickClose}>
      <ConfirmationHeader heading="Removal of Meal Selections" />
      <ConfirmationBody>
        <div className="py-16 md:py-12">
          <p className="mx-auto max-w-sm text-body-lg">
            This update removes the last {mealsDifference} meal
            {mealsDifference === 1 ? '' : 's'} you selected from your delivery.
          </p>
        </div>
      </ConfirmationBody>
      <ConfirmationButtons>
        <div className="grid grid-cols-2 gap-2">
          <Button buttonStyle="stroke" onClick={onClickClose} size="large">
            Cancel
          </Button>

          <Button onClick={onClickConfirm} size="large">
            Update Delivery
          </Button>
        </div>
      </ConfirmationButtons>
    </ConfirmationDialog>
  )
}

export const MealsUnavailableConfirmation = ({
  onClickClose,
  onClickConfirm,
  unavailableMealSelections,
}: {
  onClickClose(): void
  onClickConfirm(): void
  unavailableMealSelections: { title: string }[]
}) => {
  return (
    <ConfirmationDialog onRequestClose={onClickClose}>
      <ConfirmationHeader heading="Meal Selections Unavailable" />
      <ConfirmationBody>
        <div className="py-16 md:py-12">
          <div className="mx-auto max-w-sm space-y-4 text-body-lg">
            <p>
              The following meal selections are unavailable on this delivery
              day:
            </p>

            <div>
              {unavailableMealSelections.map((meal) => {
                return <p key={meal.title}>{meal.title}</p>
              })}
            </div>
          </div>
        </div>
      </ConfirmationBody>
      <ConfirmationButtons>
        <div className="grid grid-cols-2 gap-2">
          <Button buttonStyle="stroke" onClick={onClickClose} size="large">
            Cancel
          </Button>

          <Button onClick={onClickConfirm} size="large">
            Update Delivery Anyway
          </Button>
        </div>
      </ConfirmationButtons>
    </ConfirmationDialog>
  )
}

export const TooManyMealsAndMealsUnavailableConfirmation = ({
  onClickClose,
  onClickConfirm,
  unavailableMealSelections,
}: {
  onClickClose(): void
  onClickConfirm(): void
  unavailableMealSelections: { title: string }[]
}) => {
  return (
    <ConfirmationDialog onRequestClose={onClickClose}>
      <ConfirmationHeader heading="Meal Selections Unavailable" />
      <ConfirmationBody>
        <div className="py-16 md:py-12">
          <div className="mx-auto max-w-sm space-y-4 text-body-lg">
            <p>
              The following meal selections are unavailable on this delivery
              day:
            </p>

            <div>
              {unavailableMealSelections.map((meal) => {
                return <p key={meal.title}>{meal.title}</p>
              })}
            </div>

            <p>
              We may also need to remove meals to fit your new meals per order
              size.
            </p>
          </div>
        </div>
      </ConfirmationBody>
      <ConfirmationButtons>
        <div className="grid grid-cols-2 gap-2">
          <Button buttonStyle="stroke" onClick={onClickClose} size="large">
            Cancel
          </Button>

          <Button onClick={onClickConfirm} size="large">
            Update Delivery Anyway
          </Button>
        </div>
      </ConfirmationButtons>
    </ConfirmationDialog>
  )
}

const SkipWeekConfirmation = ({
  alternativeSubTerm,
  onClickClose,
  onClickFewerMeals,
  onClickSkip,
  onClickSwitchDelivery,
  skipWeekError,
  smallerSubscription,
}: {
  alternativeSubTerm: TermStatusSubTerm | undefined
  onClickClose(): void
  onClickFewerMeals(smallerSubscription: UserSubscriptionType): void
  onClickSkip(): void
  onClickSwitchDelivery(alternativeSubTerm: TermStatusSubTerm): void
  selectedUserTerm: UserTerm
  skipWeekError: Error | null
  smallerSubscription: UserSubscriptionType | undefined
}) => {
  let heading = 'Get Fewer Meals or Switch Your Delivery Day?'
  let message =
    'Another delivery day is available for this week. Would you like to switch delivery days or get fewer meals?'

  if (!alternativeSubTerm) {
    heading = 'Get Fewer Meals?'
    message =
      'A smaller meal plan is available for this week. Would you like to get fewer meals?'
  } else if (!smallerSubscription) {
    heading = 'Switch Your Delivery Day?'
    message =
      'Another delivery day is available for this week. Would you like to switch delivery days?'
  }

  return (
    <ConfirmationDialog onRequestClose={onClickClose}>
      <ConfirmationHeader heading={heading} />
      <ConfirmationBody>
        <div className="py-16 md:py-12">
          <p className="mx-auto max-w-sm text-body-lg">{message}</p>
        </div>
      </ConfirmationBody>
      <ConfirmationButtons>
        <div className="space-y-4">
          {skipWeekError && (
            <APIErrorDisplay
              error={skipWeekError}
              errorCodeMessageMap={SKIP_WEEK_ERRORS}
            />
          )}

          <div className="flex flex-col items-center space-y-4">
            <Button onClick={onClickSkip} size="large">
              Skip Delivery
            </Button>

            {alternativeSubTerm && (
              <Button
                buttonStyle="stroke"
                onClick={() => onClickSwitchDelivery(alternativeSubTerm)}
                size="large"
              >
                Deliver{' '}
                {formatDate(alternativeSubTerm.deliveryDate, {
                  format: DATE_FORMATS.DOW_MONTH_DAY,
                })}{' '}
                Instead
              </Button>
            )}

            {smallerSubscription && (
              <Button
                buttonStyle="stroke"
                onClick={() => onClickFewerMeals(smallerSubscription)}
                size="large"
              >
                Get Fewer Meals
              </Button>
            )}
          </div>
        </div>
      </ConfirmationButtons>
    </ConfirmationDialog>
  )
}

export const DiscardChangesConfirmation = ({
  onClickClose,
  onClickConfirm,
}: {
  onClickClose(): void
  onClickConfirm(): void
}) => {
  return (
    <ConfirmationDialog onRequestClose={onClickClose}>
      <ConfirmationHeader heading="Discard Delivery Updates?" />
      <ConfirmationBody>
        <div className="py-16 md:py-12">
          <p className="mx-auto max-w-sm text-body-lg">
            Continuing will discard your changes. Are you sure you want to leave
            this screen?
          </p>
        </div>
      </ConfirmationBody>
      <ConfirmationButtons>
        <div className="grid grid-cols-2 gap-2">
          <Button buttonStyle="stroke" onClick={onClickClose} size="large">
            Cancel
          </Button>

          <Button onClick={onClickConfirm} size="large">
            Discard Updates
          </Button>
        </div>
      </ConfirmationButtons>
    </ConfirmationDialog>
  )
}
